import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { api, type GalleryImage, type GalleryPage } from '../../lib/api'
import { AddSheet } from '../items/AddSheet'
import { TabBar } from '../../components/TabBar'
import { layout, sousLaBarre } from '../../design/tokens'

/**
 * Galerie photo — planche 16 : « grille de 3, regroupement mensuel ».
 *
 * Une image appartient toujours à une fiche : la galerie ne fait que les rassembler, elle ne
 * crée pas un second endroit où ranger des photos. Toucher une vignette ramène donc à sa fiche —
 * on retrouve l'image, on veut son contexte.
 */
export function GalleryScreen() {
  const { spaceId = '' } = useParams()
  const navigate = useNavigate()
  const [pages, setPages] = useState<string[]>([])
  const [feuille, setFeuille] = useState(false)

  const { data, isPending } = useQuery<GalleryPage>({
    queryKey: ['galerie', spaceId, pages.at(-1) ?? null],
    queryFn: () => api.gallery(spaceId, pages.at(-1)),
  })

  const [chargees, setChargees] = useState<GalleryImage[]>([])
  const images = pages.length === 0 ? (data?.images ?? []) : [...chargees, ...(data?.images ?? [])]

  return (
    <main
      className="mx-auto min-h-dvh bg-surface"
      style={{
        maxWidth: layout.column,
        paddingInline: layout.gutter,
        paddingBottom: sousLaBarre(32),
      }}
    >
      <header className="flex items-center gap-3" style={{ height: layout.header }}>
        <button
          type="button"
          onClick={() => void navigate(-1)}
          aria-label="Retour"
          className="flex shrink-0 items-center justify-center rounded-field border border-border bg-card text-card-title font-semibold"
          style={{ width: 40, height: 40 }}
        >
          ←
        </button>
        <h1 className="grow truncate font-serif text-screen">Galerie</h1>
      </header>

      {isPending && images.length === 0 ? (
        <p className="text-body text-ink-muted">Chargement…</p>
      ) : images.length > 0 ? (
        <>
          {grouperParMois(images).map((groupe) => (
            <section key={groupe.cle} className="mb-6">
              <h2 className="mb-3 text-label font-semibold uppercase text-ink-muted">
                {groupe.libelle}
              </h2>
              {/* Trois colonnes, comme la planche. Chaque vignette est un carré : une grille de
                  hauteurs inégales se lit mal et empêche de balayer du regard. */}
              <ul className="grid grid-cols-3" style={{ gap: 4 }}>
                {groupe.images.map((image) => (
                  <li key={image.id}>
                    <Link
                      to={`/espace/${spaceId}/fiche/${image.itemId}`}
                      title={image.itemTitle}
                      className="block overflow-hidden rounded-field bg-skeleton-image"
                      style={{ aspectRatio: '1 / 1' }}
                    >
                      <img
                        src={image.url}
                        alt={image.itemTitle}
                        loading="lazy"
                        className="h-full w-full object-cover"
                      />
                    </Link>
                  </li>
                ))}
              </ul>
            </section>
          ))}

          {data?.nextCursor ? (
            <button
              type="button"
              onClick={() => {
                setChargees([...images])
                setPages((deja) => [...deja, data.nextCursor!])
              }}
              className="w-full rounded-button border border-border bg-card text-card-title font-semibold"
              style={{ height: layout.primaryButtonHeight }}
            >
              Voir plus
            </button>
          ) : null}
        </>
      ) : (
        <div style={{ paddingBlock: 32 }}>
          <p className="text-body">Aucune photo pour l'instant.</p>
          <p className="mt-2 text-body text-ink-muted">
            Les images ajoutées à une fiche, ou reçues par courrier, se rassemblent ici.
          </p>
        </div>
      )}

      {/* Destination d'outil comme l'agenda ou les courses : la barre y a sa place, pour
          pouvoir sauter ailleurs sans repasser par le retour. */}
      <TabBar onAdd={() => setFeuille(true)} activeSpaceId={spaceId} />

      {feuille ? <AddSheet spaceId={spaceId} onClose={() => setFeuille(false)} /> : null}
    </main>
  )
}

type GroupeMensuel = { cle: string; libelle: string; images: GalleryImage[] }

/**
 * Regroupement par mois, en heure locale. Le libellé porte l'année dès qu'on quitte l'année en
 * cours : « juin » seul serait ambigu au bout de deux ans d'usage.
 */
function grouperParMois(images: readonly GalleryImage[]): GroupeMensuel[] {
  const groupes = new Map<string, GroupeMensuel>()
  const anneeCourante = new Date().getFullYear()

  for (const image of images) {
    const date = new Date(image.createdAt)
    const cle = `${date.getFullYear()}-${date.getMonth()}`

    let groupe = groupes.get(cle)
    if (!groupe) {
      groupe = {
        cle,
        libelle: date.toLocaleDateString('fr-FR', {
          month: 'long',
          ...(date.getFullYear() === anneeCourante ? {} : { year: 'numeric' }),
        }),
        images: [],
      }
      groupes.set(cle, groupe)
    }

    groupe.images.push(image)
  }

  return [...groupes.values()]
}
